import { useMemo, useState } from 'react';
import type {
  CpqLegacyImportCandidate,
  CpqLegacyImportPreview,
  CpqLegacyImportRunItem,
} from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { TextField } from '../../components/ui/Field.js';
import {
  useCpqLegacyImportCandidates,
  useCpqLegacyImportPreview,
  useCpqLegacyImportRun,
} from './api.js';

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/** Import runs in small sequential batches so a big list never hits the request timeout. */
const IMPORT_CHUNK_SIZE = 5;

const FREQUENCY_LABELS: Record<string, string> = {
  one_time: 'One-time',
  monthly: 'Monthly',
  yearly: 'Yearly',
};

function frequencyLabel(value: string): string {
  return FREQUENCY_LABELS[value] ?? value;
}

function parseQuoteNumbers(input: string): number[] {
  return Array.from(
    new Set(
      input
        .split(/[\s,;]+/)
        .map((part) => Number(part.trim()))
        .filter((n) => Number.isInteger(n) && n > 0),
    ),
  );
}

function errorMessage(error: unknown, fallback: string): string | null {
  if (!error) return null;
  return error instanceof ApiError ? error.message : fallback;
}

/**
 * One-time import of legacy CPQ quotes into PSA quotes: preview a few quote numbers to
 * test, then scan all CPQ quotes and import the eligible ones in batches.
 */
export function LegacyImportCard(): JSX.Element {
  const previewMutation = useCpqLegacyImportPreview();
  const candidatesMutation = useCpqLegacyImportCandidates();
  const runMutation = useCpqLegacyImportRun();

  const [numbersInput, setNumbersInput] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [results, setResults] = useState<CpqLegacyImportRunItem[]>([]);

  const candidates = candidatesMutation.data?.candidates ?? [];
  const eligibleCandidates = useMemo(() => candidates.filter((c) => c.eligible), [candidates]);
  const previews = previewMutation.data ?? [];

  const runImport = async (quoteNumbers: number[]): Promise<void> => {
    if (quoteNumbers.length === 0 || importing) return;
    setImporting(true);
    setResults([]);
    const collected: CpqLegacyImportRunItem[] = [];
    try {
      for (let i = 0; i < quoteNumbers.length; i += IMPORT_CHUNK_SIZE) {
        const chunk = quoteNumbers.slice(i, i + IMPORT_CHUNK_SIZE);
        setProgress(
          `Importing ${Math.min(i + chunk.length, quoteNumbers.length)} of ${quoteNumbers.length}…`,
        );
        try {
          const result = await runMutation.mutateAsync(chunk);
          collected.push(...result.items);
        } catch (err) {
          for (const quoteNumber of chunk) {
            collected.push({
              quoteNumber,
              status: 'failed',
              message: err instanceof ApiError ? err.message : 'The import request failed.',
              quoteId: null,
              opportunityId: null,
              opportunityName: null,
            });
          }
        }
        setResults([...collected]);
      }
    } finally {
      setImporting(false);
      setProgress(null);
    }
    // Imported opportunities are no longer eligible — refresh the scan if one is open.
    if (candidatesMutation.data) candidatesMutation.mutate();
  };

  const toggleSelected = (quoteNumber: number, checked: boolean): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(quoteNumber);
      else next.delete(quoteNumber);
      return next;
    });
  };

  const previewNumbers = parseQuoteNumbers(numbersInput);
  const eligiblePreviewNumbers = previews.filter((p) => p.eligible).map((p) => p.quoteNumber);
  const selectedNumbers = eligibleCandidates
    .map((c) => c.quoteNumber)
    .filter((n) => selected.has(n));

  const created = results.filter((r) => r.status === 'created').length;
  const skipped = results.filter((r) => r.status === 'skipped').length;
  const failed = results.filter((r) => r.status === 'failed').length;

  return (
    <Card>
      <div className="space-y-5">
        <div>
          <h2 className="text-sm font-semibold text-slate-800">Legacy quote import</h2>
          <p className="text-xs text-slate-500">
            One-time copy of legacy CPQ quotes into PSA quotes on the matched opportunity: the
            Statement of Work becomes quote sections (pictures skipped), one-time lines go to
            lines section 1 and monthly/annual lines to lines section 2. Imported quotes get a
            “Legacy CPQ Quote” banner. Opportunities that already have a PSA quote are skipped.
          </p>
        </div>

        {/* Test a few quotes before batching. */}
        <div className="rounded-lg border border-slate-200 p-3">
          <div className="flex flex-wrap items-end gap-2">
            <div className="w-72">
              <TextField
                label="Test quote numbers"
                value={numbersInput}
                onChange={(e) => setNumbersInput(e.target.value)}
                placeholder="e.g. 1663, 1681"
              />
            </div>
            <Button
              variant="secondary"
              onClick={() => previewMutation.mutate(previewNumbers)}
              isLoading={previewMutation.isPending}
              disabled={previewNumbers.length === 0 || importing}
            >
              Preview
            </Button>
            {eligiblePreviewNumbers.length > 0 ? (
              <Button
                onClick={() => void runImport(eligiblePreviewNumbers)}
                disabled={importing}
                isLoading={importing && !candidatesMutation.data}
              >
                Import {eligiblePreviewNumbers.length} previewed quote
                {eligiblePreviewNumbers.length === 1 ? '' : 's'}
              </Button>
            ) : null}
          </div>
          {errorMessage(previewMutation.error, 'Failed to preview the CPQ quotes.') ? (
            <p className="mt-2 text-sm text-rose-600">
              {errorMessage(previewMutation.error, 'Failed to preview the CPQ quotes.')}
            </p>
          ) : null}
          {previews.map((preview) => (
            <PreviewPanel key={preview.quoteNumber} preview={preview} />
          ))}
        </div>

        {/* Batch: scan everything, pick, import in chunks. */}
        <div className="rounded-lg border border-slate-200 p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-slate-500">
              Scan every CPQ quote, match it to a PSA opportunity, and import the eligible ones.
            </p>
            <Button
              variant="secondary"
              onClick={() => candidatesMutation.mutate()}
              isLoading={candidatesMutation.isPending}
              disabled={importing}
            >
              Scan all CPQ quotes
            </Button>
          </div>
          {errorMessage(candidatesMutation.error, 'Failed to scan the CPQ quotes.') ? (
            <p className="mt-2 text-sm text-rose-600">
              {errorMessage(candidatesMutation.error, 'Failed to scan the CPQ quotes.')}
            </p>
          ) : null}
          {candidatesMutation.data ? (
            <div className="mt-3 space-y-3">
              <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
                <span>
                  Scanned {candidatesMutation.data.scanned} quotes · {eligibleCandidates.length}{' '}
                  eligible
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setSelected(new Set(eligibleCandidates.map((c) => c.quoteNumber)))}
                  disabled={eligibleCandidates.length === 0 || importing}
                >
                  Select all eligible
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setSelected(new Set())}
                  disabled={selected.size === 0 || importing}
                >
                  Clear
                </Button>
                <Button
                  onClick={() => void runImport(selectedNumbers)}
                  disabled={selectedNumbers.length === 0 || importing}
                  isLoading={importing}
                >
                  {progress ?? `Import ${selectedNumbers.length} selected`}
                </Button>
              </div>
              <div className="max-h-96 overflow-y-auto overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-2 py-2 font-medium" />
                      <th className="px-2 py-2 font-medium">Quote #</th>
                      <th className="px-2 py-2 font-medium">Name</th>
                      <th className="px-2 py-2 font-medium">Account</th>
                      <th className="px-2 py-2 font-medium">Opportunity</th>
                      <th className="px-2 py-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {candidates.map((candidate) => (
                      <CandidateRow
                        key={candidate.quoteNumber}
                        candidate={candidate}
                        checked={selected.has(candidate.quoteNumber)}
                        disabled={importing}
                        onToggle={toggleSelected}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>

        {results.length > 0 ? (
          <div className="rounded-lg border border-slate-200 p-3">
            <p className="text-sm font-medium text-slate-700">
              Import results: {created} created · {skipped} skipped · {failed} failed
            </p>
            <ul className="mt-2 space-y-1 text-sm">
              {results.map((result) => (
                <li key={result.quoteNumber} className="flex flex-wrap items-center gap-2">
                  <Badge
                    tone={
                      result.status === 'created'
                        ? 'green'
                        : result.status === 'skipped'
                          ? 'amber'
                          : 'red'
                    }
                  >
                    {result.status}
                  </Badge>
                  <span className="tabular-nums text-slate-600">#{result.quoteNumber}</span>
                  {result.opportunityName ? (
                    <span className="text-slate-500">→ {result.opportunityName}</span>
                  ) : null}
                  {result.quoteId ? (
                    <a
                      href={`/sales/quotes/${result.quoteId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand-600 underline"
                    >
                      Open PSA quote
                    </a>
                  ) : null}
                  {result.message ? <span className="text-slate-500">{result.message}</span> : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

function CandidateRow({
  candidate,
  checked,
  disabled,
  onToggle,
}: {
  candidate: CpqLegacyImportCandidate;
  checked: boolean;
  disabled: boolean;
  onToggle: (quoteNumber: number, checked: boolean) => void;
}): JSX.Element {
  return (
    <tr className={candidate.eligible ? '' : 'opacity-60'}>
      <td className="px-2 py-1.5">
        {candidate.eligible ? (
          <input
            type="checkbox"
            checked={checked}
            disabled={disabled}
            onChange={(e) => onToggle(candidate.quoteNumber, e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
          />
        ) : null}
      </td>
      <td className="px-2 py-1.5 tabular-nums text-slate-600">{candidate.quoteNumber}</td>
      <td className="px-2 py-1.5 text-slate-800">{candidate.quoteName ?? '—'}</td>
      <td className="px-2 py-1.5 text-slate-600">{candidate.accountName ?? '—'}</td>
      <td className="px-2 py-1.5 text-slate-600">{candidate.opportunityName ?? '—'}</td>
      <td className="px-2 py-1.5">
        {candidate.eligible ? (
          <Badge tone="green">Eligible</Badge>
        ) : (
          <span className="text-xs text-slate-500">{candidate.reason}</span>
        )}
      </td>
    </tr>
  );
}

function PreviewPanel({ preview }: { preview: CpqLegacyImportPreview }): JSX.Element {
  const lines1 = preview.lines.filter((l) => l.lineGroup === 1);
  const lines2 = preview.lines.filter((l) => l.lineGroup === 2);
  return (
    <div className="mt-3 rounded-lg bg-slate-50 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={preview.eligible ? 'green' : 'red'}>
          {preview.eligible ? 'Ready to import' : 'Not importable'}
        </Badge>
        <span className="text-sm font-medium text-slate-800">
          CPQ #{preview.quoteNumber} {preview.quoteName ? `— ${preview.quoteName}` : ''}
        </span>
        {preview.opportunityName ? (
          <span className="text-sm text-slate-500">
            → {preview.opportunityName}
            {preview.customerName ? ` (${preview.customerName})` : ''}
          </span>
        ) : null}
      </div>
      {preview.message ? <p className="mt-1 text-sm text-rose-600">{preview.message}</p> : null}
      {preview.eligible ? (
        <div className="mt-2 space-y-3 text-sm">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Sections ({preview.sections.length})
            </p>
            <ul className="mt-1 space-y-0.5">
              {preview.sections.map((section) => (
                <li key={section.sectionIndex} className="text-slate-600">
                  <span className="tabular-nums text-slate-400">{section.sectionIndex}.</span>{' '}
                  {section.title ? (
                    <span className="font-medium text-slate-700">{section.title} — </span>
                  ) : null}
                  <span className="text-slate-500">{section.snippet || '(empty)'}</span>
                </li>
              ))}
            </ul>
          </div>
          <LinesPreviewTable
            title={`Lines 1 · ${frequencyLabel(preview.lines1Frequency)} · total ${currency.format(Number(preview.lines1Total))}`}
            lines={lines1}
          />
          <LinesPreviewTable
            title={`Lines 2 · ${frequencyLabel(preview.lines2Frequency)} · total ${currency.format(Number(preview.lines2Total))}`}
            lines={lines2}
          />
        </div>
      ) : null}
    </div>
  );
}

function LinesPreviewTable({
  title,
  lines,
}: {
  title: string;
  lines: CpqLegacyImportPreview['lines'];
}): JSX.Element | null {
  if (lines.length === 0) return null;
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</p>
      <table className="mt-1 w-full text-left text-sm">
        <thead className="text-xs uppercase tracking-wide text-slate-400">
          <tr>
            <th className="py-1 pr-2 font-medium">Description</th>
            <th className="py-1 pr-2 text-right font-medium">Qty</th>
            <th className="py-1 pr-2 text-right font-medium">Unit price</th>
            <th className="py-1 font-medium">Product</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {lines.map((line, index) => (
            <tr key={index}>
              {line.isComment ? (
                <td className="py-1 pr-2 italic text-slate-500" colSpan={4}>
                  {line.description}
                </td>
              ) : (
                <>
                  <td className="py-1 pr-2 text-slate-700">
                    {line.description}
                    {line.isOptional ? (
                      <span className="ml-2 inline-flex">
                        <Badge tone="blue">Optional</Badge>
                      </span>
                    ) : null}
                  </td>
                  <td className="py-1 pr-2 text-right tabular-nums text-slate-600">
                    {line.quantity}
                  </td>
                  <td className="py-1 pr-2 text-right tabular-nums text-slate-600">
                    {currency.format(Number(line.unitPrice))}
                  </td>
                  <td className="py-1 text-xs text-slate-500">
                    {line.productId ? 'Matched by SKU' : line.sku ? 'Custom (SKU kept)' : 'Custom'}
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
